import { useState, useEffect } from "react";
import { useConfirm } from "@/components/ConfirmProvider";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import useBranches from "@/hooks/useBranches";
import { EMPTY_CUSTOMER_FORM } from "@/lib/constants";
import { INDIAN_STATES, RELATION_TYPES, GENDER_OPTIONS, PIN_RE, customerPin } from "@/lib/india";
import Drawer, { FormField, FormSelect, FormTextarea, getSaveLabel } from "@/components/Drawer";
import DocumentList from "@/components/DocumentList";
import BranchSelect from "@/components/BranchSelect";

function customerToForm(c) {
  // Pull every supported field, defaulting to empty string for safety.
  return {
    ...EMPTY_CUSTOMER_FORM,
    name: c.name || "",
    first_name: c.first_name || "",
    last_name: c.last_name || "",
    relation_type: c.relation_type || "",
    relation_name: c.relation_name || "",
    gender: c.gender || "",
    dob: c.dob || "",
    email: c.email || "",
    phone: c.phone || "",
    alternate_phone: c.alternate_phone || "",
    house_no: c.house_no || "",
    street: c.street || "",
    town_village: c.town_village || "",
    mandal: c.mandal || "",
    district: c.district || "",
    state: c.state || "",
    pin_code: c.pin_code || customerPin(c),
    country: c.country || "India",
    address: c.address || "",
    aadhar_no: c.aadhar_no || "",
    pan_no: c.pan_no || "",
    driving_licence_no: c.driving_licence_no || "",
    nominee_name: c.nominee_name || "",
    nominee_relation: c.nominee_relation || "",
    nominee_age: c.nominee_age || "",
    customer_type: c.customer_type || "direct",
    gst_number: c.gst_number || "",
    notes: c.notes || "",
    documents: c.documents || [],
    branch_id: c.branch_id || "",
  };
}

function SectionTitle({ index, title, hint }) {
  return (
    <div className="flex items-baseline justify-between border-b border-canvas-line pb-2 mt-2">
      <div>
        <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-ink-muted mr-2">
          {index}
        </span>
        <span className="font-display text-sm font-semibold text-ink">{title}</span>
      </div>
      {hint && <span className="font-mono text-[10px] text-ink-subtle">{hint}</span>}
    </div>
  );
}

export default function CustomerFormDrawer({ open, editing, onClose, onSaved }) {
  const { user } = useAuth();
  const { branches } = useBranches({ activeOnly: true });
  const [form, setForm] = useState(EMPTY_CUSTOMER_FORM);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm(customerToForm(editing));
      return;
    }
    // Default branch: user.branch_id for staff, first active branch for admin.
    const defaultBranch =
      user?.branch_id || (branches.length ? branches[0].id : "");
    setForm({ ...EMPTY_CUSTOMER_FORM, branch_id: defaultBranch });
  }, [open, editing, user, branches]);

  // Auto-mirror first+last → name unless user has typed a custom display name.
  const setField = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      if (k === "first_name" || k === "last_name") {
        const composed = `${next.first_name || ""} ${next.last_name || ""}`.trim();
        // Only auto-replace name if it currently matches the previously-composed value
        // (i.e. user hasn't customised it). Always set when name is empty.
        const prevComposed = `${f.first_name || ""} ${f.last_name || ""}`.trim();
        if (!f.name || f.name === prevComposed) next.name = composed;
      }
      return next;
    });
  };

  const hasKyc = !!(
    (form.aadhar_no || "").trim() ||
    (form.pan_no || "").trim() ||
    (form.driving_licence_no || "").trim()
  );

  const confirm = useConfirm();
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!PIN_RE.test((form.pin_code || "").trim())) {
      toast.error("PIN code is required (6 digits).");
      return;
    }
    if (!(await confirm({ title: "Save customer?", body: "Customer details will be saved to the master. Confirm before continuing." }))) return;
    if (!form.name.trim()) {
      toast.error("Customer name is required.");
      return;
    }
    if (!form.phone.trim()) {
      toast.error("Mobile number is required.");
      return;
    }
    if (!hasKyc) {
      toast.error("At least one KYC ID is required (Aadhar / PAN / Driving Licence).");
      return;
    }
    if (form.customer_type === "business" && !(form.gst_number || "").trim()) {
      toast.error("GST Number is mandatory for Business (B2B) customers.");
      return;
    }
    setSaving(true);
    try {
      // Coerce nominee_age string → int (or null). Backend rejects "23" but
      // accepts 23 due to Pydantic int validation.
      const payload = { ...form };
      const ageStr = String(payload.nominee_age || "").trim();
      payload.nominee_age = ageStr ? parseInt(ageStr, 10) : null;
      if (editing) {
        await api.put(`/customers/${editing.id}`, payload);
        toast.success("Customer updated");
      } else {
        await api.post("/customers", payload);
        toast.success("Customer added");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const relationOptions = RELATION_TYPES;
  const genderOptions = GENDER_OPTIONS;
  const stateOptions = [{ value: "", label: "—" }, ...INDIAN_STATES.map((s) => ({ value: s, label: s }))];

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow={`${editing ? "Edit" : "New"} Customer`}
      title={editing ? editing.name : "Add to registry"}
      testIdClose="close-customer-form"
    >
      <form onSubmit={handleSubmit} data-testid="customer-form" className="space-y-4">
        {/* IDENTITY */}
        <SectionTitle index="01" title="Identity" />
        <div className="grid grid-cols-2 gap-3">
          <FormField
            label="First Name"
            value={form.first_name}
            onChange={(v) => setField("first_name", v)}
            testId="customer-first-name"
            required
          />
          <FormField
            label="Last Name / Surname"
            value={form.last_name}
            onChange={(v) => setField("last_name", v)}
            testId="customer-last-name"
          />
        </div>
        <FormField
          label="Customer Name (display)"
          value={form.name}
          onChange={(v) => setField("name", v)}
          testId="customer-name"
          required
        />
        <div className="grid grid-cols-2 gap-3">
          <FormSelect
            label="Relation"
            value={form.relation_type}
            onChange={(v) => setField("relation_type", v)}
            options={relationOptions}
            testId="customer-relation-type"
          />
          <FormField
            label="Relation Name"
            value={form.relation_name}
            onChange={(v) => setField("relation_name", v)}
            testId="customer-relation-name"
            placeholder="Father / Husband / Guardian"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormSelect
            label="Gender"
            value={form.gender}
            onChange={(v) => setField("gender", v)}
            options={genderOptions}
            testId="customer-gender"
          />
          <FormField
            label="Date of Birth"
            type="date"
            value={form.dob}
            onChange={(v) => setField("dob", v)}
            testId="customer-dob"
          />
        </div>

        {/* CONTACT */}
        <SectionTitle index="02" title="Contact" />
        <div className="grid grid-cols-2 gap-3">
          <FormField
            label="Mobile"
            value={form.phone}
            onChange={(v) => setField("phone", v)}
            testId="customer-phone"
            required
          />
          <FormField
            label="Alternate Mobile"
            value={form.alternate_phone}
            onChange={(v) => setField("alternate_phone", v)}
            testId="customer-alt-phone"
          />
        </div>
        <FormField
          label="Email (optional)"
          type="email"
          value={form.email}
          onChange={(v) => setField("email", v)}
          testId="customer-email"
        />

        {/* ADDRESS */}
        <SectionTitle index="03" title="Address" hint="Stored as structured + composed string" />
        <div className="grid grid-cols-3 gap-3">
          <FormField
            label="House No"
            value={form.house_no}
            onChange={(v) => setField("house_no", v)}
            testId="customer-house-no"
          />
          <FormField
            label="Street"
            value={form.street}
            onChange={(v) => setField("street", v)}
            testId="customer-street"
          />
          <FormField
            label="Town / Village"
            value={form.town_village}
            onChange={(v) => setField("town_village", v)}
            testId="customer-town"
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <FormField
            label="Mandal"
            value={form.mandal}
            onChange={(v) => setField("mandal", v)}
            testId="customer-mandal"
          />
          <FormField
            label="District"
            value={form.district}
            onChange={(v) => setField("district", v)}
            testId="customer-district"
          />
          <FormField
            label="PIN Code *"
            value={form.pin_code}
            onChange={(v) => setField("pin_code", v.replace(/\D/g, "").slice(0, 6))}
            testId="customer-pin"
            placeholder="6 digits"
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <FormSelect
            label="State"
            value={form.state}
            onChange={(v) => setField("state", v)}
            options={stateOptions}
            testId="customer-state"
          />
          <FormField
            label="Country"
            value={form.country}
            onChange={(v) => setField("country", v)}
            testId="customer-country"
          />
        </div>
        <FormTextarea
          label="Full Address (auto-composed if blank)"
          value={form.address}
          onChange={(v) => setField("address", v)}
          testId="customer-address"
        />

        {/* KYC */}
        <SectionTitle
          index="04"
          title="KYC"
          hint={hasKyc ? "At least one provided" : "Required: provide at least 1"}
        />
        <div className="grid grid-cols-3 gap-3">
          <FormField
            label="Aadhar No"
            value={form.aadhar_no}
            onChange={(v) => setField("aadhar_no", v)}
            testId="customer-aadhar"
          />
          <FormField
            label="PAN No"
            value={form.pan_no}
            onChange={(v) => setField("pan_no", v)}
            testId="customer-pan"
          />
          <FormField
            label="Driving Licence"
            value={form.driving_licence_no}
            onChange={(v) => setField("driving_licence_no", v)}
            testId="customer-dl"
          />
        </div>
        {!hasKyc && (
          <div
            className="font-mono text-[10px] text-destructive border border-destructive/40 bg-destructive/5 px-2 py-1.5"
            data-testid="customer-kyc-required-hint"
          >
            ⚠ Provide at least one KYC ID before saving.
          </div>
        )}

        {/* NOMINEE — required by insurance + RTO forms post-sale. */}
        <SectionTitle
          index="05"
          title="Nominee"
          hint="For insurance & registration paperwork"
        />
        <div className="grid grid-cols-3 gap-3">
          <FormField
            label="Nominee Name"
            value={form.nominee_name}
            onChange={(v) => setField("nominee_name", v)}
            testId="customer-nominee-name"
          />
          <FormSelect
            label="Relation"
            value={form.nominee_relation || ""}
            onChange={(v) => setField("nominee_relation", v)}
            options={[
              { value: "", label: "—" },
              { value: "wife", label: "Wife" },
              { value: "husband", label: "Husband" },
              { value: "son", label: "Son" },
              { value: "daughter", label: "Daughter" },
              { value: "father", label: "Father" },
              { value: "mother", label: "Mother" },
              { value: "brother", label: "Brother" },
              { value: "sister", label: "Sister" },
              { value: "other", label: "Other" },
            ]}
            testId="customer-nominee-relation"
          />
          <FormField
            label="Nominee Age"
            value={form.nominee_age}
            onChange={(v) => setField("nominee_age", v.replace(/[^0-9]/g, "").slice(0, 3))}
            testId="customer-nominee-age"
          />
        </div>

        {/* CUSTOMER TYPE */}
        <SectionTitle
          index="06"
          title="Customer Type"
          hint={form.customer_type === "business" ? "B2B · GST mandatory" : "B2C retail"}
        />
        <div className="grid grid-cols-2 gap-3">
          <FormSelect
            label="Type"
            value={form.customer_type || "direct"}
            onChange={(v) => setField("customer_type", v)}
            options={[
              { value: "direct", label: "Direct (B2C)" },
              { value: "business", label: "Business (B2B)" },
            ]}
            testId="customer-type"
          />
          {form.customer_type === "business" && (
            <FormField
              label="GST Number *"
              value={form.gst_number}
              onChange={(v) => setField("gst_number", v.toUpperCase())}
              required
              testId="customer-gst"
            />
          )}
        </div>

        {/* META */}
        <SectionTitle index="07" title="Branch & notes" />
        <BranchSelect
          value={form.branch_id}
          onChange={(v) => setField("branch_id", v)}
          testId="customer-branch"
        />
        <FormTextarea
          label="General Notes"
          value={form.notes}
          onChange={(v) => setField("notes", v)}
          testId="customer-general-notes"
        />
        <DocumentList
          documents={form.documents}
          onChange={(documents) => setField("documents", documents)}
        />

        <div className="pt-4 flex items-center gap-2 justify-end border-t border-canvas-line -mx-6 px-6 sticky bottom-0 bg-canvas-card">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 border border-canvas-line text-sm hover:border-ink"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            data-testid="save-customer-button"
            className="px-4 py-2.5 bg-ink text-white text-sm font-semibold hover:bg-brand transition-colors disabled:opacity-60"
          >
            {getSaveLabel(saving, editing, "Saving…", "Save changes", "Add customer")}
          </button>
        </div>
      </form>
    </Drawer>
  );
}
